import { useQuery } from '@tanstack/react-query';
import config from 'config';
import { useAuthContext } from 'modules/auth/context';
import * as Api from '../api';

interface IProps {
  enabled?: boolean;
}

/**
 * The caller's notebooks in the active org, newest-updated first. The first call also adopts old
 * documents into the default notebook.
 *
 * Notebooks are per org, so the key carries the org the request is sent for: switching org is a
 * different cache entry and never shows the previous org's grid. The org id is read the way the
 * HTTP client picks the `X-Organization-Id` header (localStorage first, then the auth context), so
 * the key and the request can't disagree mid-switch. `['notebooks']` stays the prefix that
 * create/rename/delete invalidate.
 */
const useList = ({ enabled = true }: IProps = {}) => {
  const { state } = useAuthContext();
  const orgId = localStorage.getItem(config.api.active_org_key) || state.organization?.id || null;

  const query = useQuery({
    queryKey: ['notebooks', 'list', orgId],
    queryFn: async () => {
      const response = await Api.list();
      return response.data.data;
    },
    enabled: enabled && Boolean(orgId),
  });

  return {
    notebooks: query.data ?? [],
    // Until an org is known the query is idle; that must not read as "no notebooks".
    isLoading: query.isLoading || (enabled && !orgId),
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};

export default useList;
